import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = ['all', 'myths', 'symbolism', 'stories', 'aftercare'];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div data-testid="blog-list-page" className="tattoo-bg-functional min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        
        {/* Hero Section */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-7xl md:text-8xl font-bold mb-4 leading-none">
              TATTOO<br />JOURNAL
            </h1>
            <p className="font-mono text-sm uppercase tracking-widest text-gray-600">INSIGHTS & STORIES</p>
          </motion.div>
        </section>

        {/* Category Filter */}
        <div className="mb-16" data-testid="category-filter">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`category-${category}`}
                onClick={() => setSelectedCategory(category)}
                className={`px-8 py-3 font-bold text-sm uppercase tracking-wider transition-all ${
                  selectedCategory === category
                    ? 'bg-black text-white'
                    : 'border-2 border-black text-black hover:bg-black hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Posts */}
        {loading ? (
          <div className="text-center py-12">
            <p className="font-body text-lg">Loading posts...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-body text-lg text-gray-600">No posts found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Link 
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-${post.id}`}
                  className="block group"
                >
                  {post.cover_image && (
                    <div className="polaroid mb-4">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-64 object-cover"
                      />
                    </div>
                  )}
                  <p className="font-mono text-xs uppercase tracking-widest text-gray-600 mb-2">
                    {post.category}
                  </p>
                  <h2 className="font-heading text-2xl font-bold mb-2 group-hover:underline">
                    {post.title}
                  </h2>
                  <p className="font-body text-base text-gray-600 line-clamp-3">
                    {post.excerpt}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default BlogListPage;